import { scopeProjectRef, scopeThreadRef } from "@t3tools/client-runtime/environment";
import { AsyncResult } from "effect/unstable/reactivity";
import {
  BoxIcon,
  CheckCircle2Icon,
  CircleAlertIcon,
  CircleXIcon,
  DatabaseIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  ProjectVmProfileProposal,
  ProjectVmProfile,
  ProjectVmProfileConfig,
  ProjectVmPort,
  ProjectVmSecretSummary,
  ProjectVmVerification,
  ThreadId,
  VmBuild,
  VmBuildState,
} from "@t3tools/contracts";
import { PROJECT_VM_PROFILE_PROPOSAL_PATH } from "@t3tools/contracts";
import type { SidebarProjectGroupMember } from "../../sidebarProjectGrouping";
import {
  isAtomCommandInterrupted,
  settlePromise,
  squashAtomCommandFailure,
  type AtomCommandResult,
} from "@t3tools/client-runtime/state/runtime";

import { useComposerDraftStore } from "../../composerDraftStore";
import { useNewThreadHandler } from "../../hooks/useHandleNewThread";
import { readLocalApi } from "../../localApi";
import { useThreadShellsForProjectRefs } from "../../state/entities";
import { useEnvironmentQuery } from "../../state/query";
import { vcsEnvironment } from "../../state/vcs";
import { vmEnvironment } from "../../state/vm";
import { useAtomCommand } from "../../state/use-atom-command";
import { useVmExecutionStore } from "../../vmExecutionStore";
import { useRightPanelStore } from "../../rightPanelStore";
import {
  formatVmSetupWorkspaceSource,
  resolveVmSetupWorkspaceSource,
} from "../../vmSetupWorkspace";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { stackedThreadToast, toastManager } from "../ui/toast";
import { SettingsRow, SettingsSection } from "./settingsLayout";
import { VmSecretsDialog, type VmSecretEntry } from "./VmSecretsDialog";
import {
  appendVmSettingsRowIdentity,
  createVmSettingsRowIdentityState,
  formatVmBuildError,
  getVmSnapshotBlockReason,
  getVmProfileProposalEligibility,
  preserveVmProfileSecretNames,
  removeVmSettingsRowIdentity,
  selectVmStatusCandidates,
  vmProfileProposalRefreshKey,
  VM_SNAPSHOT_BLOCKED_COPY,
} from "./ProjectVmSettings.logic";

const DEFAULT_VM_CONFIG: ProjectVmProfileConfig = {
  install: "true",
  updateContent: "true",
  start: "true",
  resources: {
    vcpu: 2,
    memoryMiB: 4096,
    diskGiB: 30,
  },
  ports: [],
  verification: [],
  secretNames: [],
};

const DEFAULT_VM_NAME = "VM environment";
const VM_STATUS_QUERY_CONCURRENCY = 8;
const VM_STATUS_QUERY_LIMIT = 32;

const VM_SETUP_PROMPT = [
  "Set up this project's reusable virtual-machine environment.",
  "",
  "The exact target is a Debian bookworm Linux/arm64 guest running under Apple Container. The target process runs as root, and the project workspace is /mnt/workspace. The available base tools/packages include bash ca-certificates chromium curl ffmpeg git imagemagick node npm openbox tar x11vnc xdotool xz-utils xvfb.",
  "Declared project ports are metadata only. Fabrica Code does not currently proxy them or assign application URLs, and it never sets NEXT_PUBLIC_APP_URL automatically. A service can still be opened from the VM Desktop at its guest-local URL. Configure any required origin explicitly, usually http://localhost:<guestPort>, and do not claim host-browser access through a declared port.",
  "Inspect this isolated setup workspace. Read AGENTS.md, README files, package manifests, and lockfiles, then identify the project's language, package manager, build/test commands, and how its development server is started. The workspace must contain only a clean committed Git HEAD: no dirty or untracked source files from the user's checkout. The VM does not exist yet, so never claim VM validation from host-side checks or claim you inspected anything inside the guest.",
  "Do not leave tracked or untracked source changes in the setup workspace except .t3/vm-profile-proposal.json. Ignored dependency and cache files are allowed. If the project requires source edits, generated project files, or base packages unavailable in the target image, emit a blocked proposal and explain each requirement instead of changing the project.",
  "",
  "Prepare and validate a repeatable recipe for these four fields:",
  "1. install: a finite dependency/bootstrap command used to prepare a reusable VM Build.",
  "2. updateContent: a command that refreshes dependencies or generated files after the task checkout moves to its exact commit.",
  "3. start: the normal foreground command for the project's long-running development services. Fabrica Code runs it as a managed guest process, so do not add shell backgrounding.",
  "4. verification: separate bounded commands that prove the environment is healthy. Include a real health check against the project's development service when one exists; do not use a placeholder such as true when a service check is possible.",
  "",
  "Run safe, idempotent checks in this setup workspace where useful, and record the commands you actually ran in checksRun. Keep every verification command finite and separately bounded. Do not create a snapshot automatically. Do not read, print, or commit secret values; leave .env files, credentials, and private keys out of the recipe. Proposal secretNames contain names only, never values.",
  "",
  `Before your final response, create ${PROJECT_VM_PROFILE_PROPOSAL_PATH}. Fabrica Code reads this file to populate the Environment surface, so do not only paste the proposal into chat. Write strict JSON with exactly one of these shapes. Ready example:`,
  '{"schemaVersion":1,"status":"ready","name":"Project development","config":{"install":"finite install command","updateContent":"finite refresh command","start":"foreground development command","resources":{"vcpu":2,"memoryMiB":4096,"diskGiB":30},"ports":[{"name":"web","guestPort":3000,"protocol":"http","visibility":"private"}],"verification":[{"name":"Health check","command":"curl --fail http://127.0.0.1:3000/health","timeoutSeconds":60}],"secretNames":["EXAMPLE_SECRET_NAME"]},"requirements":{"basePackages":[],"projectChanges":[]},"checksRun":["git status --short"],"assumptions":["The service binds to the guest loopback interface."]}',
  "Blocked example:",
  '{"schemaVersion":1,"status":"blocked","reason":"The project requires a source change and an unavailable base package.","requirements":{"basePackages":["unavailable-package"],"projectChanges":["Add the generated project config before starting the service."]},"checksRun":["git status --short","node --version"],"assumptions":["The target image does not include unavailable-package."]}',
  "Use empty arrays when no ports, checks, secrets, base packages, project changes, or assumptions are needed. If project changes or unavailable base packages are required, status must be blocked and config/name must be omitted. After writing the file, summarize what you proposed and which checks you actually ran in this setup workspace; never present host-side checks as VM validation.",
].join("\n");

const VM_BUILD_PROMPT = [
  "Prepare this saved VM environment as a reusable Build.",
  "",
  "You are inside the actual Debian bookworm Linux/arm64 Apple Container guest as root, with the project checked out at /mnt/workspace. This is the Build-preparation task opened from Fabrica Code's Environment surface, not a host-side setup workspace.",
  "Read /opt/t3/build-setup/profile.json and /opt/t3/build-setup/result.json first. profile.json contains the saved recipe and secret names only. result.json reports whether Fabrica Code's automatic install, content update, managed start, and verification pass succeeded; when it failed, it includes the failed operation and message.",
  "",
  "Inspect the actual guest and make the saved recipe reliable. If the automatic pass failed, reproduce and diagnose that failure inside this guest. You may revise install, updateContent, start, resources, ports, verification, and secretNames through the proposal file. Keep install and updateContent finite and idempotent. start must be the normal foreground command for the long-running development services; do not add shell backgrounding. Validation commands may safely manage temporary processes when needed.",
  "Declared ports are metadata only. Fabrica Code does not proxy them or assign application URLs. Test services through guest-local addresses such as http://127.0.0.1:<guestPort>, and do not invent NEXT_PUBLIC_APP_URL or claim host-browser access.",
  "Do not intentionally edit tracked or untracked project source in /mnt/workspace; only write .t3/vm-profile-proposal.json. Ignored dependencies, build outputs, and caches are allowed. The managed start command may itself regenerate tracked project files while it runs. That transient runtime output is not a required project change: every new VM task resets source to its exact committed revision before updateContent and start run. Do not hide or commit those generated changes. If correctness requires a source change that is not already committed, return a blocked proposal and explain it. Repeatable system-package installation may be added to the install recipe when appropriate.",
  "Never read, print, or expose secret values. You may use already-injected secrets to run the project, but the proposal must contain secret names only.",
  "Leave Fabrica Code's managed development process running when you finish. Stop only temporary validation processes that you started yourself; do not stop the t3-project-start process. Fabrica Code runs the saved start command again before snapshot verification, but the Environment surface should remain usable while the user reviews your result.",
  "",
  "Run bounded checks in this guest. A ready result requires the finite preparation commands to succeed, the development service to start, and meaningful verification commands to pass against the actual guest. Do not create the snapshot yourself; Fabrica Code enables Create Build in the Environment surface after this task is ready.",
  "",
  `Before your final response, always write ${PROJECT_VM_PROFILE_PROPOSAL_PATH}. Fabrica Code reads it directly from this VM and applies it back to the same Environment surface. Use exactly one of these strict JSON shapes. Ready example:`,
  '{"schemaVersion":1,"status":"ready","name":"Project development","config":{"install":"finite install command","updateContent":"finite refresh command","start":"foreground development command","resources":{"vcpu":2,"memoryMiB":4096,"diskGiB":30},"ports":[{"name":"web","guestPort":3000,"protocol":"http","visibility":"private"}],"verification":[{"name":"Health check","command":"curl --fail http://127.0.0.1:3000/health","timeoutSeconds":60}],"secretNames":["EXAMPLE_SECRET_NAME"]},"requirements":{"basePackages":[],"projectChanges":[]},"checksRun":["commands actually run inside the guest"],"assumptions":[]}',
  "Blocked example:",
  '{"schemaVersion":1,"status":"blocked","reason":"The environment still requires a committed project change.","requirements":{"basePackages":[],"projectChanges":["Describe the required committed change."]},"checksRun":["commands actually run inside the guest"],"assumptions":[]}',
  "Use empty arrays when a list is empty. Copy the current saved config into a ready proposal when no recipe changes are needed. Use blocked unless your guest-side evidence proves the environment is ready. In the final response, summarize the actual guest checks and whether Fabrica Code can create the Build.",
].join("\n");

function configForProfile(profile: ProjectVmProfile | null | undefined): ProjectVmProfileConfig {
  if (profile?.config === undefined) return DEFAULT_VM_CONFIG;

  // Profiles created before VM-managed secrets do not have this field in the
  // persisted projection. Keep those profiles editable without forcing a new
  // version merely to normalize their shape.
  return {
    ...profile.config,
    resources: {
      ...profile.config.resources,
      diskGiB: profile.config.resources.diskGiB ?? DEFAULT_VM_CONFIG.resources.diskGiB,
    },
    secretNames: Array.isArray(profile.config.secretNames) ? profile.config.secretNames : [],
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "An error occurred.";
}

function formatBuildDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function buildStateVariant(
  state: VmBuildState,
): "default" | "success" | "warning" | "error" | "outline" {
  switch (state) {
    case "ready":
      return "success";
    case "failed":
      return "error";
    case "queued":
    case "preparing":
    case "installing":
    case "verifying":
    case "snapshotting":
    case "restore-testing":
    case "deleting":
      return "warning";
    case "superseded":
      return "outline";
  }
}

function buildStateLabel(state: VmBuildState): string {
  return state === "restore-testing" ? "restore test" : state;
}

function ProposalDetailList({
  label,
  items,
  emptyCopy,
}: {
  readonly label: string;
  readonly items: ReadonlyArray<string>;
  readonly emptyCopy?: string | undefined;
}) {
  if (items.length === 0 && emptyCopy === undefined) return null;
  return (
    <div className="mt-2">
      <p className="text-xs font-medium text-foreground">{label}</p>
      {items.length > 0 ? (
        <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          {items.map((item) => (
            <li key={`${label}-${item}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">{emptyCopy}</p>
      )}
    </div>
  );
}

type ProjectVmSettingsProject = Pick<
  SidebarProjectGroupMember,
  "environmentId" | "id" | "vmProfile" | "workspaceRoot"
>;

export function ProjectVmSettings({
  project,
  proposalThreadId,
  proposalCompletedAt = null,
  proposalRunning = false,
  buildPreparationActive = false,
  buildPreparationBootstrapping = false,
}: {
  project: ProjectVmSettingsProject;
  proposalThreadId?: ThreadId;
  proposalCompletedAt?: string | null;
  proposalRunning?: boolean;
  buildPreparationActive?: boolean;
  buildPreparationBootstrapping?: boolean;
}) {
  const handleNewThread = useNewThreadHandler();
  const getProfile = useAtomCommand(vmEnvironment.profileGet, { reportFailure: false });
  const configureProfile = useAtomCommand(vmEnvironment.profileConfigure, {
    reportFailure: false,
  });
  const getProfileProposal = useAtomCommand(vmEnvironment.profileProposalGet, {
    reportFailure: false,
  });
  const listSecrets = useAtomCommand(vmEnvironment.secretsList, { reportFailure: false });
  const putSecrets = useAtomCommand(vmEnvironment.secretsPut, { reportFailure: false });
  const deleteSecret = useAtomCommand(vmEnvironment.secretDelete, { reportFailure: false });
  const listBuilds = useAtomCommand(vmEnvironment.buildsList, { reportFailure: false });
  const snapshotBuild = useAtomCommand(vmEnvironment.buildSnapshot, { reportFailure: false });
  const activateBuild = useAtomCommand(vmEnvironment.buildActivate, { reportFailure: false });
  const deleteBuild = useAtomCommand(vmEnvironment.buildDelete, { reportFailure: false });
  const getVmStatus = useAtomCommand(vmEnvironment.status, { reportFailure: false });
  const setupSourceQuery = useEnvironmentQuery(
    vcsEnvironment.status({
      environmentId: project.environmentId,
      input: { cwd: project.workspaceRoot },
    }),
  );
  const setupSource = useMemo(
    () => resolveVmSetupWorkspaceSource(setupSourceQuery.data),
    [setupSourceQuery.data],
  );
  // VCS status is a live subscription, so its AsyncResult can remain waiting
  // after the initial local snapshot is already available.
  const isSetupSourcePending = setupSourceQuery.data === null && setupSourceQuery.isPending;

  const [profile, setProfile] = useState<ProjectVmProfile | null>(project.vmProfile ?? null);
  const [builds, setBuilds] = useState<ReadonlyArray<VmBuild>>(project.vmProfile?.builds ?? []);
  const [activeBuildId, setActiveBuildId] = useState<string | null>(
    project.vmProfile?.activeBuildId ?? null,
  );
  const [name, setName] = useState(project.vmProfile?.name ?? DEFAULT_VM_NAME);
  const [config, setConfig] = useState<ProjectVmProfileConfig>(() =>
    configForProfile(project.vmProfile ?? null),
  );
  const [verificationRowIdentity, setVerificationRowIdentity] = useState(() =>
    createVmSettingsRowIdentityState(
      "verification",
      configForProfile(project.vmProfile ?? null).verification.length,
    ),
  );
  const [portRowIdentity, setPortRowIdentity] = useState(() =>
    createVmSettingsRowIdentityState(
      "port",
      configForProfile(project.vmProfile ?? null).ports.length,
    ),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [proposal, setProposal] = useState<ProjectVmProfileProposal | null>(null);
  const [proposalStatus, setProposalStatus] = useState<
    "idle" | "loading" | "loaded" | "missing" | "error"
  >("idle");
  const [proposalWorkspaceChanges, setProposalWorkspaceChanges] = useState<ReadonlyArray<string>>(
    [],
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [secrets, setSecrets] = useState<ReadonlyArray<ProjectVmSecretSummary>>(() =>
    (project.vmProfile?.config.secretNames ?? []).map((secretName) => ({
      name: secretName,
      configured: false,
    })),
  );
  const [secretsDialogOpen, setSecretsDialogOpen] = useState(false);
  const [busySecretName, setBusySecretName] = useState<string | null>(null);
  const [busyBuildId, setBusyBuildId] = useState<string | null>(null);
  const [snapshotBusyThreadId, setSnapshotBusyThreadId] = useState<string | null>(null);
  const [runningVms, setRunningVms] = useState<
    ReadonlyArray<{ threadId: ThreadId; title: string }>
  >([]);
  const [runningVmsLoaded, setRunningVmsLoaded] = useState(false);
  const buildPreparationVmDetected =
    buildPreparationActive &&
    proposalThreadId !== undefined &&
    runningVms.some((runningVm) => runningVm.threadId === proposalThreadId);
  const nameErrorId = useId();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const profileRefreshIdRef = useRef(0);
  const runningVmRefreshIdRef = useRef(0);
  const proposalRefreshKeyRef = useRef<string | null>(null);

  const target = useMemo(
    () => ({ environmentId: project.environmentId, input: { projectId: project.id } }),
    [project.environmentId, project.id],
  );
  const projectRefs = useMemo(
    () => [scopeProjectRef(project.environmentId, project.id)],
    [project.environmentId, project.id],
  );
  const projectThreads = useThreadShellsForProjectRefs(projectRefs);
  const snapshotGuardNow = new Date().toISOString();
  const failedBuildRetryVm = runningVms.length === 1 ? (runningVms[0] ?? null) : null;
  const failedBuildRetryThread =
    failedBuildRetryVm === null
      ? undefined
      : projectThreads.find((thread) => thread.id === failedBuildRetryVm.threadId);
  const failedBuildRetryBlocked =
    failedBuildRetryThread === undefined
      ? false
      : getVmSnapshotBlockReason(failedBuildRetryThread, { now: snapshotGuardNow }) !== null;
  const hasUnsavedProfileChanges =
    profile === null ||
    name.trim() !== profile.name ||
    JSON.stringify(config) !== JSON.stringify(configForProfile(profile));

  const reportFailure = useCallback(
    (title: string, result: AtomCommandResult<unknown, unknown>) => {
      if (result._tag !== "Failure" || isAtomCommandInterrupted(result)) return;
      const error = squashAtomCommandFailure(result);
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title,
          description: errorMessage(error),
        }),
      );
    },
    [],
  );

  const applyState = useCallback((nextProfile: ProjectVmProfile | null) => {
    const nextConfig = configForProfile(nextProfile);
    setProfile(nextProfile);
    setName(nextProfile?.name ?? DEFAULT_VM_NAME);
    setConfig(nextConfig);
    setVerificationRowIdentity(
      createVmSettingsRowIdentityState("verification", nextConfig.verification.length),
    );
    setPortRowIdentity(createVmSettingsRowIdentityState("port", nextConfig.ports.length));
    setBuilds(nextProfile?.builds ?? []);
    setActiveBuildId(nextProfile?.activeBuildId ?? null);
  }, []);

  const applyProposal = useCallback(
    (nextProposal: ProjectVmProfileProposal, workspaceChanges: ReadonlyArray<string>) => {
      setProposal(nextProposal);
      setProposalWorkspaceChanges(workspaceChanges);
      setProposalStatus("loaded");
      const eligibility = getVmProfileProposalEligibility({
        proposal: nextProposal,
        workspaceChanges,
        taskSourceResetsAtBootstrap: buildPreparationActive,
      });
      if (!eligibility.canApply || nextProposal.status !== "ready") return;
      setName(nextProposal.name);
      setConfig((current) =>
        preserveVmProfileSecretNames(nextProposal.config, [
          ...(profile?.config.secretNames ?? []),
          ...current.secretNames,
        ]),
      );
      setVerificationRowIdentity(
        createVmSettingsRowIdentityState("verification", nextProposal.config.verification.length),
      );
      setPortRowIdentity(
        createVmSettingsRowIdentityState("port", nextProposal.config.ports.length),
      );
    },
    [buildPreparationActive, profile?.config.secretNames],
  );

  const applySecretProfileState = useCallback((nextProfile: ProjectVmProfile) => {
    setProfile(nextProfile);
    setBuilds(nextProfile.builds);
    setActiveBuildId(nextProfile.activeBuildId);
    setConfig((current) => ({
      ...current,
      secretNames: nextProfile.config.secretNames,
    }));
  }, []);

  const refresh = useCallback(async () => {
    const refreshId = ++profileRefreshIdRef.current;
    setIsLoading(true);
    try {
      const [profileResult, buildsResult, secretsResult] = await Promise.all([
        getProfile(target),
        listBuilds(target),
        listSecrets(target),
      ]);
      if (profileRefreshIdRef.current !== refreshId) return;
      if (AsyncResult.isSuccess(profileResult)) {
        applyState(profileResult.value.profile ?? null);
      } else {
        reportFailure("Failed to load VM profile", profileResult);
      }
      if (AsyncResult.isSuccess(buildsResult)) {
        setBuilds(buildsResult.value.builds ?? []);
        setActiveBuildId(buildsResult.value.activeBuildId ?? null);
      } else {
        reportFailure("Failed to load VM Builds", buildsResult);
      }
      if (AsyncResult.isSuccess(secretsResult)) {
        setSecrets(secretsResult.value.secrets ?? []);
      } else {
        reportFailure("Failed to load VM secrets", secretsResult);
      }
    } finally {
      if (profileRefreshIdRef.current === refreshId) setIsLoading(false);
    }
  }, [applyState, getProfile, listBuilds, listSecrets, reportFailure, target]);

  const loadAgentProposal = useCallback(
    async (showMissingNotice = false) => {
      if (!proposalThreadId) return;
      setProposalStatus("loading");
      const result = await getProfileProposal({
        environmentId: project.environmentId,
        input: { projectId: project.id, threadId: proposalThreadId },
      });
      if (AsyncResult.isFailure(result)) {
        setProposalStatus("error");
        reportFailure("Failed to load the agent's VM proposal", result);
        return;
      }
      if (result.value.proposal === null) {
        setProposal(null);
        setProposalWorkspaceChanges(result.value.workspaceChanges ?? []);
        setProposalStatus("missing");
        if (showMissingNotice) {
          toastManager.add({
            type: "warning",
            title: "No VM proposal found",
            description: `Ask the setup agent to write ${PROJECT_VM_PROFILE_PROPOSAL_PATH}, then reload the proposal.`,
          });
        }
        return;
      }
      applyProposal(result.value.proposal, result.value.workspaceChanges ?? []);
    },
    [
      applyProposal,
      getProfileProposal,
      project.environmentId,
      project.id,
      proposalThreadId,
      reportFailure,
    ],
  );

  const refreshRunningVms = useCallback(async () => {
    const refreshId = ++runningVmRefreshIdRef.current;
    const candidates = selectVmStatusCandidates(projectThreads, VM_STATUS_QUERY_LIMIT).map(
      (thread) => ({
        environmentId: thread.environmentId,
        threadId: thread.id,
        title: thread.title,
      }),
    );
    if (
      buildPreparationActive &&
      proposalThreadId !== undefined &&
      !candidates.some((candidate) => candidate.threadId === proposalThreadId)
    ) {
      candidates.unshift({
        environmentId: project.environmentId,
        threadId: proposalThreadId,
        title: "Build preparation",
      });
    }
    const statuses: Array<{ threadId: ThreadId; title: string }> = [];
    for (let offset = 0; offset < candidates.length; offset += VM_STATUS_QUERY_CONCURRENCY) {
      const batch = await Promise.all(
        candidates.slice(offset, offset + VM_STATUS_QUERY_CONCURRENCY).map(async (thread) => {
          const result = await getVmStatus({
            environmentId: thread.environmentId,
            input: { threadId: thread.threadId },
          });
          if (!AsyncResult.isSuccess(result)) return null;
          const status = result.value;
          if (status.state !== "running" || !status.available || status.sandboxId.length === 0) {
            return null;
          }
          return { threadId: thread.threadId, title: thread.title };
        }),
      );
      if (runningVmRefreshIdRef.current !== refreshId) return;
      statuses.push(...batch.filter((status) => status !== null));
    }
    if (runningVmRefreshIdRef.current === refreshId) {
      setRunningVms(statuses);
      setRunningVmsLoaded(true);
    }
  }, [
    buildPreparationActive,
    getVmStatus,
    project.environmentId,
    projectThreads,
    proposalThreadId,
  ]);

  useEffect(() => {
    applyState(project.vmProfile ?? null);
    void refresh();
    return () => {
      profileRefreshIdRef.current += 1;
    };
  }, [applyState, project.vmProfile, refresh]);

  useEffect(() => {
    void refreshRunningVms();
    if (!buildPreparationBootstrapping || buildPreparationVmDetected) {
      return () => {
        runningVmRefreshIdRef.current += 1;
      };
    }
    const intervalId = window.setInterval(() => void refreshRunningVms(), 2_000);
    return () => {
      window.clearInterval(intervalId);
      runningVmRefreshIdRef.current += 1;
    };
  }, [buildPreparationBootstrapping, buildPreparationVmDetected, refreshRunningVms]);

  useEffect(() => {
    if (isLoading) return;
    const refreshKey = vmProfileProposalRefreshKey({
      threadId: proposalThreadId,
      completedAt: proposalCompletedAt,
      running: proposalRunning,
      profileUpdatedAt: profile?.updatedAt ?? null,
    });
    if (refreshKey === null || proposalRefreshKeyRef.current === refreshKey) return;
    proposalRefreshKeyRef.current = refreshKey;
    void loadAgentProposal();
  }, [
    isLoading,
    loadAgentProposal,
    profile?.updatedAt,
    proposalCompletedAt,
    proposalRunning,
    proposalThreadId,
  ]);

  const proposalEligibility = useMemo(
    () =>
      getVmProfileProposalEligibility({
        proposal,
        workspaceChanges: proposalWorkspaceChanges,
        taskSourceResetsAtBootstrap: buildPreparationActive,
      }),
    [buildPreparationActive, proposal, proposalWorkspaceChanges],
  );
  const proposalSaveBlocked =
    proposalStatus === "loaded" && proposal !== null && !proposalEligibility.canSave;

  const updateConfig = useCallback((update: Partial<ProjectVmProfileConfig>) => {
    setConfig((current) => ({ ...current, ...update }));
  }, []);

  const updateVerification = useCallback(
    (index: number, update: Partial<ProjectVmVerification>) => {
      setConfig((current) => ({
        ...current,
        verification: current.verification.map((verification, candidateIndex) =>
          candidateIndex === index ? { ...verification, ...update } : verification,
        ),
      }));
    },
    [],
  );

  const addVerification = useCallback(() => {
    setVerificationRowIdentity((current) => appendVmSettingsRowIdentity(current, "verification"));
    setConfig((current) => ({
      ...current,
      verification: [
        ...current.verification,
        { name: "Check", command: "true", timeoutSeconds: 300 },
      ],
    }));
  }, []);

  const removeVerification = useCallback((index: number) => {
    setVerificationRowIdentity((current) => removeVmSettingsRowIdentity(current, index));
    setConfig((current) => ({
      ...current,
      verification: current.verification.filter((_, candidateIndex) => candidateIndex !== index),
    }));
  }, []);

  const updatePort = useCallback((index: number, update: Partial<ProjectVmPort>) => {
    setConfig((current) => ({
      ...current,
      ports: current.ports.map((port, candidateIndex) =>
        candidateIndex === index ? { ...port, ...update } : port,
      ),
    }));
  }, []);

  const addPort = useCallback(() => {
    setPortRowIdentity((current) => appendVmSettingsRowIdentity(current, "port"));
    setConfig((current) => ({
      ...current,
      ports: [
        ...current.ports,
        { name: "web", guestPort: 3000, protocol: "http", visibility: "private" },
      ],
    }));
  }, []);

  const removePort = useCallback((index: number) => {
    setPortRowIdentity((current) => removeVmSettingsRowIdentity(current, index));
    setConfig((current) => ({
      ...current,
      ports: current.ports.filter((_, candidateIndex) => candidateIndex !== index),
    }));
  }, []);

  const saveSecrets = useCallback(
    async (entries: ReadonlyArray<VmSecretEntry>) => {
      if (proposalSaveBlocked) {
        throw new Error("Resolve the blocked VM proposal before changing its secrets.");
      }
      const trimmedName = name.trim();
      if (!trimmedName) {
        setNameError("Enter a name for this VM profile.");
        nameInputRef.current?.focus();
        throw new Error("The VM environment needs a name before secrets can be saved.");
      }
      const addedNames = entries.map((entry) => entry.name);
      const nextConfig = preserveVmProfileSecretNames(config, addedNames);
      const persistedSecretNames = profile?.config.secretNames ?? [];
      const declaresNewSecret =
        profile === null ||
        addedNames.some((secretName) => !persistedSecretNames.includes(secretName));
      if (declaresNewSecret) {
        const profileResult = await configureProfile({
          environmentId: project.environmentId,
          input: {
            projectId: project.id,
            name: trimmedName,
            config: nextConfig,
          },
        });
        if (AsyncResult.isFailure(profileResult)) {
          reportFailure("Failed to update the VM environment", profileResult);
          throw squashAtomCommandFailure(profileResult);
        }
        const configuredProfile = profileResult.value.profile;
        if (configuredProfile === null) {
          throw new Error("The server did not return the configured VM environment.");
        }
        applySecretProfileState(configuredProfile);
      }
      const result = await putSecrets({
        environmentId: project.environmentId,
        input: { projectId: project.id, secrets: entries },
      });
      if (AsyncResult.isFailure(result)) {
        reportFailure("Failed to save VM secrets", result);
        throw squashAtomCommandFailure(result);
      }
      setSecrets((current) => {
        const byName = new Map(current.map((secret) => [secret.name, secret] as const));
        for (const secret of result.value.secrets) byName.set(secret.name, secret);
        return Array.from(byName.values()).toSorted((left, right) =>
          left.name.localeCompare(right.name),
        );
      });
      toastManager.add({
        type: "success",
        title: entries.length === 1 ? "VM secret saved" : `${entries.length} VM secrets saved`,
        description: "The values will be available to new VM tasks for this environment.",
      });
    },
    [
      applySecretProfileState,
      config,
      configureProfile,
      name,
      profile,
      project.environmentId,
      project.id,
      putSecrets,
      proposalSaveBlocked,
      reportFailure,
    ],
  );

  const removeSecret = useCallback(
    async (secretName: string) => {
      setBusySecretName(secretName);
      try {
        const result = await deleteSecret({
          environmentId: project.environmentId,
          input: { projectId: project.id, name: secretName },
        });
        if (AsyncResult.isFailure(result)) {
          reportFailure("Failed to remove VM secret", result);
          return;
        }
        const persistedConfig = profile?.config ?? config;
        const nextConfig = {
          ...persistedConfig,
          secretNames: persistedConfig.secretNames.filter((candidate) => candidate !== secretName),
        };
        const profileResult = await configureProfile({
          environmentId: project.environmentId,
          input: {
            projectId: project.id,
            name: (profile?.name ?? name.trim()) || DEFAULT_VM_NAME,
            config: nextConfig,
          },
        });
        if (AsyncResult.isFailure(profileResult)) {
          setSecrets(result.value.secrets);
          reportFailure("Failed to update the VM environment", profileResult);
          return;
        }
        const configuredProfile = profileResult.value.profile;
        if (configuredProfile === null) {
          setSecrets(result.value.secrets);
          toastManager.add({
            type: "error",
            title: "Failed to update the VM environment",
            description: "The server did not return the configured VM environment.",
          });
          return;
        }
        applySecretProfileState(configuredProfile);
        setSecrets((current) => {
          const byName = new Map(
            current
              .filter((secret) => secret.name !== secretName)
              .map((secret) => [secret.name, secret] as const),
          );
          for (const secret of result.value.secrets) {
            if (secret.name !== secretName) byName.set(secret.name, secret);
          }
          return Array.from(byName.values());
        });
      } finally {
        setBusySecretName(null);
      }
    },
    [
      applySecretProfileState,
      config,
      configureProfile,
      deleteSecret,
      name,
      profile,
      project.environmentId,
      project.id,
      reportFailure,
    ],
  );

  const save = useCallback(async (): Promise<boolean> => {
    if (proposalSaveBlocked) {
      toastManager.add({
        type: "warning",
        title: "VM proposal needs attention",
        description:
          "Resolve the setup proposal requirements and reload it before saving this environment.",
      });
      return false;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Enter a name for this VM profile.");
      nameInputRef.current?.focus();
      return false;
    }
    setNameError(null);
    setIsSaving(true);
    try {
      const result = await configureProfile({
        environmentId: project.environmentId,
        input: {
          projectId: project.id,
          name: trimmedName,
          config,
        },
      });
      if (AsyncResult.isFailure(result)) {
        reportFailure("Failed to save VM profile", result);
        return false;
      }
      if (result.value.profile === null) {
        toastManager.add({
          type: "error",
          title: "Failed to save VM profile",
          description: "The server did not return the saved VM environment.",
        });
        return false;
      }
      applyState(result.value.profile);
      setProposal(null);
      setProposalStatus("idle");
      toastManager.add({
        type: "success",
        title: profile === null ? "VM profile created" : "VM profile saved",
      });
      return true;
    } finally {
      setIsSaving(false);
    }
  }, [
    applyState,
    config,
    configureProfile,
    name,
    profile,
    project.environmentId,
    project.id,
    proposalSaveBlocked,
    reportFailure,
  ]);

  const runBuildAction = useCallback(
    async (build: VmBuild, action: "activate" | "delete") => {
      setBusyBuildId(build.id);
      try {
        const command = action === "activate" ? activateBuild : deleteBuild;
        const result = await command({
          environmentId: project.environmentId,
          input: { projectId: project.id, buildId: build.id },
        });
        if (AsyncResult.isFailure(result)) {
          reportFailure(`Failed to ${action} VM Build`, result);
          return;
        }
        applyState(result.value.profile);
        toastManager.add({
          type: "success",
          title: action === "activate" ? "VM Build activated" : "VM Build deleted",
        });
      } finally {
        setBusyBuildId(null);
      }
    },
    [activateBuild, applyState, deleteBuild, project.environmentId, project.id, reportFailure],
  );

  const confirmDeleteBuild = useCallback(
    async (build: VmBuild) => {
      const api = readLocalApi();
      if (!api) return;
      const confirmation = await settlePromise(() =>
        api.dialogs.confirm(
          [
            `Delete VM Build ${build.id}?`,
            "This removes the reusable VM image and cannot be undone.",
          ].join("\n"),
          { variant: "destructive" },
        ),
      );
      if (confirmation._tag === "Failure" || !confirmation.value) return;
      await runBuildAction(build, "delete");
    },
    [runBuildAction],
  );

  const createSnapshot = useCallback(
    async (threadId: ThreadId) => {
      setSnapshotBusyThreadId(threadId);
      try {
        const result = await snapshotBuild({
          environmentId: project.environmentId,
          input: { projectId: project.id, threadId },
        });
        if (AsyncResult.isFailure(result)) {
          reportFailure("Failed to create VM Build", result);
          return;
        }
        await refresh();
        toastManager.add({
          type: "success",
          title: "VM Build candidate created",
          description: "Review it below and activate it when you are ready.",
        });
      } finally {
        setSnapshotBusyThreadId(null);
      }
    },
    [project.environmentId, project.id, refresh, reportFailure, snapshotBuild],
  );

  const setupWithAgent = useCallback(async () => {
    if (setupSource === null) {
      toastManager.add({
        type: "warning",
        title: "Setup workspace unavailable",
        description:
          "Check out a branch with at least one commit, then try again. VM setup will not run in an ambiguous current checkout.",
      });
      return;
    }
    const projectRef = scopeProjectRef(project.environmentId, project.id);
    const opened = await handleNewThread(projectRef, {
      envMode: "worktree",
      branch: setupSource.branch,
      startFromOrigin: setupSource.startFromOrigin,
    });
    if (!opened) return;
    const threadRef = scopeThreadRef(project.environmentId, opened.threadId);
    useComposerDraftStore.getState().setPrompt(threadRef, VM_SETUP_PROMPT);
    useVmExecutionStore.getState().setMode(threadRef, "setup");
    useRightPanelStore.getState().open(threadRef, "environment");
  }, [handleNewThread, project.environmentId, project.id, setupSource]);

  const prepareBuildWithAgent = useCallback(async () => {
    if (setupSource === null) {
      toastManager.add({
        type: "warning",
        title: "Build source unavailable",
        description:
          "Check out a branch with at least one commit, then try again. A reusable Build must start from an exact committed revision.",
      });
      return;
    }
    if (hasUnsavedProfileChanges && !(await save())) return;
    const projectRef = scopeProjectRef(project.environmentId, project.id);
    const opened = await handleNewThread(projectRef, {
      envMode: "local",
      branch: setupSource.branch,
      startFromOrigin: setupSource.startFromOrigin,
    });
    if (!opened) return;
    const threadRef = scopeThreadRef(project.environmentId, opened.threadId);
    useComposerDraftStore.getState().setPrompt(threadRef, VM_BUILD_PROMPT);
    useVmExecutionStore.getState().setMode(threadRef, "build");
    useRightPanelStore.getState().open(threadRef, "environment");
  }, [
    handleNewThread,
    hasUnsavedProfileChanges,
    project.environmentId,
    project.id,
    save,
    setupSource,
  ]);

  return (
    <SettingsSection
      id="virtual-machine"
      title="Virtual machine"
      icon={<BoxIcon aria-hidden className="size-4 text-muted-foreground" />}
      headerAction={
        <Button
          size="xs"
          disabled={isSaving || isLoading || proposalSaveBlocked}
          onClick={() => void save()}
        >
          {isSaving ? "Saving…" : proposalSaveBlocked ? "Resolve proposal" : "Save environment"}
        </Button>
      }
      aria-busy={isLoading}
    >
      <span className="sr-only" role="status" aria-live="polite">
        {isLoading ? "Loading virtual machine settings." : "Virtual machine settings loaded."}
      </span>
      {isLoading ? (
        <p className="px-3 text-sm text-muted-foreground sm:px-4" role="status">
          Loading VM settings…
        </p>
      ) : null}
      {proposalStatus === "loaded" && proposal ? (
        <div
          className={`mx-3 rounded-lg border px-3 py-2.5 text-sm sm:mx-4 ${
            proposalEligibility.canApply
              ? "border-success/30 bg-success/8"
              : proposal.status === "blocked"
                ? "border-destructive/30 bg-destructive/8"
                : "border-warning/30 bg-warning/8"
          }`}
          role="status"
        >
          <p className="flex items-center gap-2 font-medium">
            {proposalEligibility.canApply ? (
              <CheckCircle2Icon aria-hidden className="size-4 text-success" />
            ) : proposal.status === "blocked" ? (
              <CircleXIcon aria-hidden className="size-4 text-destructive" />
            ) : (
              <CircleAlertIcon aria-hidden className="size-4 text-warning" />
            )}
            {proposalEligibility.canApply
              ? "Agent proposal loaded"
              : proposal.status === "blocked"
                ? "Agent proposal blocked"
                : "Agent proposal cannot be applied"}
          </p>
          {proposal.status === "blocked" ? (
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              <span className="font-medium text-foreground">Reason:</span> {proposal.reason}
            </p>
          ) : proposalEligibility.reason === "workspace-changes" ? (
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              The setup workspace is not a clean committed checkout, so this proposal is not
              reproducible. Reload it after returning the workspace to a clean Git HEAD.
            </p>
          ) : buildPreparationActive && proposalWorkspaceChanges.length > 0 ? (
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              The running service generated the paths below in this preparation VM. New VM tasks
              reset project source to their exact commit before applying the saved recipe.
            </p>
          ) : proposalEligibility.reason === "project-changes" ? (
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              The proposal requires project changes, so Fabrica Code will not apply it
              automatically.
            </p>
          ) : (
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Review the populated commands, resources, ports, and checks, then save the
              environment.
            </p>
          )}
          <ProposalDetailList
            label="Required base packages"
            items={proposal.requirements.basePackages}
            emptyCopy={proposalEligibility.canApply ? undefined : "None required."}
          />
          <ProposalDetailList
            label="Required project changes"
            items={proposal.requirements.projectChanges}
            emptyCopy={proposalEligibility.canApply ? undefined : "None required."}
          />
          <ProposalDetailList
            label="Workspace changed paths"
            items={proposalWorkspaceChanges}
            emptyCopy={
              proposalEligibility.canApply ? undefined : "None. The setup worktree is clean."
            }
          />
          <ProposalDetailList
            label="Checks run"
            items={proposal.checksRun}
            emptyCopy={proposalEligibility.canApply ? undefined : "None recorded."}
          />
          <ProposalDetailList
            label="Assumptions"
            items={proposal.assumptions}
            emptyCopy={proposalEligibility.canApply ? undefined : "None recorded."}
          />
        </div>
      ) : proposalStatus === "missing" ? (
        <p className="mx-3 rounded-lg border border-warning/30 bg-warning/8 px-3 py-2.5 text-xs text-muted-foreground sm:mx-4">
          The setup agent finished without a structured VM proposal. Ask it to write{" "}
          <code>{PROJECT_VM_PROFILE_PROPOSAL_PATH}</code>, then reload the proposal.
        </p>
      ) : proposalStatus === "error" ? (
        <p className="mx-3 rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2.5 text-xs text-muted-foreground sm:mx-4">
          Fabrica Code could not validate the setup agent&apos;s proposal. Correct the proposal file
          and reload it.
        </p>
      ) : null}
      <fieldset disabled={isLoading || isSaving} aria-busy={isLoading} className="contents">
        <legend className="sr-only">Virtual machine profile</legend>
        <SettingsRow
          title="Profile"
          description={
            profile
              ? `Version ${profile.version}. Changes create a new profile version and do not alter existing Builds.`
              : "This project has no VM profile yet. Save the recipe below before starting a VM task."
          }
          control={
            <div className="w-full sm:w-64">
              <Input
                ref={nameInputRef}
                value={name}
                aria-label="VM profile name"
                aria-invalid={nameError !== null}
                aria-describedby={nameError === null ? undefined : nameErrorId}
                onChange={(event) => {
                  setName(event.target.value);
                  if (nameError !== null) setNameError(null);
                }}
                className="w-full"
              />
              {nameError ? (
                <p id={nameErrorId} role="alert" className="mt-1.5 text-xs text-destructive">
                  {nameError}
                </p>
              ) : null}
            </div>
          }
        />
        <SettingsRow
          title="Install command"
          description="Runs while preparing a reusable Build. Keep it repeatable and finite."
          control={
            <Textarea
              value={config.install}
              aria-label="VM install command"
              onChange={(event) => updateConfig({ install: event.target.value })}
              className="w-full min-w-64 sm:w-80"
              rows={2}
            />
          }
        />
        <SettingsRow
          title="Update content command"
          description="Runs after a task checkout moves to its exact source revision."
          control={
            <Textarea
              value={config.updateContent}
              aria-label="VM update content command"
              onChange={(event) => updateConfig({ updateContent: event.target.value })}
              className="w-full min-w-64 sm:w-80"
              rows={2}
            />
          }
        />
        <SettingsRow
          title="Start command"
          description="Runs as a managed guest process. Use the normal foreground command without shell backgrounding."
          control={
            <Textarea
              value={config.start}
              aria-label="VM start command"
              onChange={(event) => updateConfig({ start: event.target.value })}
              className="w-full min-w-64 sm:w-80"
              rows={2}
            />
          }
        />
        <SettingsRow
          title="Resources"
          description="Resources reserved for each project VM."
          control={
            <div className="grid w-full grid-cols-2 gap-2 sm:w-80 sm:grid-cols-3">
              <label className="space-y-1 text-xs text-muted-foreground">
                <span>CPU</span>
                <Input
                  type="number"
                  min={1}
                  max={16}
                  value={config.resources.vcpu}
                  aria-label="VM CPU cores"
                  onChange={(event) =>
                    updateConfig({
                      resources: { ...config.resources, vcpu: Number(event.target.value) || 1 },
                    })
                  }
                />
              </label>
              <label className="space-y-1 text-xs text-muted-foreground">
                <span>Memory MiB</span>
                <Input
                  type="number"
                  min={1024}
                  max={65536}
                  step={512}
                  value={config.resources.memoryMiB}
                  aria-label="VM memory in MiB"
                  onChange={(event) =>
                    updateConfig({
                      resources: {
                        ...config.resources,
                        memoryMiB: Number(event.target.value) || 1024,
                      },
                    })
                  }
                />
              </label>
              <label className="space-y-1 text-xs text-muted-foreground">
                <span>Disk GiB</span>
                <Input
                  type="number"
                  min={8}
                  max={2048}
                  value={config.resources.diskGiB ?? DEFAULT_VM_CONFIG.resources.diskGiB}
                  aria-label="VM disk in GiB"
                  onChange={(event) =>
                    updateConfig({
                      resources: {
                        ...config.resources,
                        diskGiB: Number(event.target.value) || 8,
                      },
                    })
                  }
                />
              </label>
            </div>
          }
        />
        <SettingsRow
          title="Verification"
          description="Finite checks run before a VM Build is accepted. Keep each command bounded and avoid printing secrets."
          control={
            <Button size="xs" variant="outline" onClick={addVerification}>
              <PlusIcon aria-hidden /> Add check
            </Button>
          }
        >
          {config.verification.length === 0 ? (
            <p className="pt-2 text-xs text-muted-foreground">No verification checks configured.</p>
          ) : (
            <div className="mt-2 space-y-2">
              {config.verification.map((verification, index) => (
                <div
                  key={verificationRowIdentity.ids[index]}
                  className="grid gap-2 rounded-lg border border-border/70 p-2 sm:grid-cols-[minmax(8rem,0.7fr)_minmax(12rem,1.5fr)_6rem_auto]"
                >
                  <Input
                    value={verification.name}
                    aria-label={`Verification ${index + 1} name`}
                    placeholder="Name"
                    onChange={(event) => updateVerification(index, { name: event.target.value })}
                  />
                  <Input
                    value={verification.command}
                    aria-label={`Verification ${index + 1} command`}
                    placeholder="Command"
                    onChange={(event) => updateVerification(index, { command: event.target.value })}
                  />
                  <Input
                    type="number"
                    min={1}
                    max={3600}
                    value={verification.timeoutSeconds ?? ""}
                    aria-label={`Verification ${index + 1} timeout in seconds`}
                    placeholder="Timeout (s)"
                    onChange={(event) =>
                      updateVerification(index, {
                        timeoutSeconds:
                          event.target.value.length === 0 ? undefined : Number(event.target.value),
                      })
                    }
                  />
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    aria-label={`Remove verification ${index + 1}`}
                    onClick={() => removeVerification(index)}
                  >
                    <Trash2Icon aria-hidden className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </SettingsRow>
        <SettingsRow
          title="Ports"
          description="Records the guest ports this project expects. Authenticated preview proxying is not available yet."
          control={
            <Button size="xs" variant="outline" onClick={addPort}>
              <PlusIcon aria-hidden /> Add port
            </Button>
          }
        >
          {config.ports.length === 0 ? (
            <p className="pt-2 text-xs text-muted-foreground">No guest ports configured.</p>
          ) : (
            <div className="mt-2 space-y-2">
              {config.ports.map((port, index) => (
                <div
                  key={portRowIdentity.ids[index]}
                  className="grid gap-2 rounded-lg border border-border/70 p-2 sm:grid-cols-[minmax(8rem,1fr)_6rem_7rem_7rem_auto]"
                >
                  <Input
                    value={port.name}
                    aria-label={`Port ${index + 1} name`}
                    placeholder="Name"
                    onChange={(event) => updatePort(index, { name: event.target.value })}
                  />
                  <Input
                    type="number"
                    min={1}
                    max={65535}
                    value={port.guestPort}
                    aria-label={`Port ${index + 1} guest port`}
                    onChange={(event) =>
                      updatePort(index, { guestPort: Number(event.target.value) || 1 })
                    }
                  />
                  <select
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                    value={port.protocol}
                    aria-label={`Port ${index + 1} protocol`}
                    onChange={(event) =>
                      updatePort(index, {
                        protocol: event.target.value as ProjectVmPort["protocol"],
                      })
                    }
                  >
                    <option value="http">HTTP</option>
                    <option value="tcp">TCP</option>
                  </select>
                  <select
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                    value={port.visibility}
                    aria-label={`Port ${index + 1} visibility`}
                    onChange={(event) =>
                      updatePort(index, {
                        visibility: event.target.value as ProjectVmPort["visibility"],
                      })
                    }
                  >
                    <option value="private">Private</option>
                    <option value="project">Project</option>
                  </select>
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    aria-label={`Remove port ${index + 1}`}
                    onClick={() => removePort(index)}
                  >
                    <Trash2Icon aria-hidden className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </SettingsRow>
        <SettingsRow
          title="Secrets"
          description="Values are stored outside the project history and VM snapshots, then injected only when this environment runs."
          control={
            <Button
              size="xs"
              variant="outline"
              disabled={proposalSaveBlocked}
              onClick={() => setSecretsDialogOpen(true)}
            >
              <PlusIcon aria-hidden /> Configure secrets
            </Button>
          }
        >
          {config.secretNames.length === 0 ? (
            <p className="pt-2 text-sm text-muted-foreground">No secrets configured.</p>
          ) : (
            <div className="mt-2 divide-y divide-border/60 overflow-hidden rounded-lg border border-border/70">
              {config.secretNames.map((secretName) => {
                const configured =
                  secrets.find((secret) => secret.name === secretName)?.configured ?? false;
                return (
                  <div key={secretName} className="flex items-center gap-3 px-3 py-2.5">
                    <code className="min-w-0 flex-1 truncate text-xs">{secretName}</code>
                    <Badge size="sm" variant={configured ? "success" : "warning"}>
                      {configured ? "Configured" : "Missing value"}
                    </Badge>
                    <Button
                      size="icon-xs"
                      variant="ghost"
                      disabled={busySecretName !== null || proposalSaveBlocked}
                      aria-label={`Remove VM secret ${secretName}`}
                      onClick={() => void removeSecret(secretName)}
                    >
                      <Trash2Icon aria-hidden className="size-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </SettingsRow>
        <SettingsRow
          title="Agent setup"
          description={
            isSetupSourcePending
              ? "Resolving a clean repository source for the setup workspace."
              : `${formatVmSetupWorkspaceSource(setupSource)}. The agent writes a structured proposal that populates this surface for review.`
          }
          control={
            <div className="flex flex-wrap justify-end gap-2">
              {proposalThreadId ? (
                <Button
                  size="xs"
                  variant="outline"
                  disabled={proposalStatus === "loading" || proposalRunning}
                  onClick={() => void loadAgentProposal(true)}
                >
                  {proposalStatus === "loading"
                    ? "Loading proposal…"
                    : proposalRunning
                      ? "Agent is working…"
                      : "Reload proposal"}
                </Button>
              ) : null}
              <Button
                size="xs"
                variant="outline"
                disabled={isSetupSourcePending}
                onClick={() => void setupWithAgent()}
              >
                {isSetupSourcePending ? "Resolving source…" : "Set up with agent"}
              </Button>
            </div>
          }
        />

        <SettingsRow
          title="Builds"
          description="Immutable prepared VM images. A new task uses the active ready Build when one exists."
          status={
            profile === null ? (
              <span className="inline-flex items-center gap-1.5">
                <CircleAlertIcon aria-hidden className="size-3.5" /> Save a profile before creating
                a Build.
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <DatabaseIcon aria-hidden className="size-3.5" />
                {activeBuildId ? "An active Build is selected." : "No active Build yet."}
              </span>
            )
          }
        >
          {builds.length === 0 ? (
            <p className="pt-2 text-sm text-muted-foreground">
              {profile === null
                ? "Save the recipe, then prepare and verify its first reusable Build here."
                : "No Builds yet. Prepare one with an agent in the actual VM, then create it from this surface."}
            </p>
          ) : (
            <div className="mt-2 divide-y divide-border/60 overflow-hidden rounded-lg border border-border/70">
              {builds.map((build) => {
                const isActive = build.id === activeBuildId;
                const busy = busyBuildId === build.id;
                const canActivate = build.state === "ready" || build.state === "superseded";
                const canDelete =
                  !isActive && build.state !== "snapshotting" && build.state !== "deleting";
                return (
                  <div key={build.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <code className="max-w-56 truncate font-mono text-xs">{build.id}</code>
                        <Badge size="sm" variant={buildStateVariant(build.state)}>
                          {buildStateLabel(build.state)}
                        </Badge>
                        {isActive ? (
                          <Badge size="sm" variant="success">
                            <CheckCircle2Icon aria-hidden /> Active
                          </Badge>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Profile v{build.profileVersion} · {formatBuildDate(build.updatedAt)}
                      </p>
                      {build.error ? (
                        <p className="mt-1 text-xs text-destructive">
                          {formatVmBuildError(build.error)}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {canActivate ? (
                        <Button
                          size="xs"
                          variant={isActive ? "secondary" : "outline"}
                          disabled={busy || isActive}
                          onClick={() => void runBuildAction(build, "activate")}
                          aria-label={`Activate VM Build ${build.id}`}
                        >
                          {isActive ? "Active" : "Activate"}
                        </Button>
                      ) : null}
                      {build.state === "failed" ? (
                        <Button
                          size="xs"
                          variant="outline"
                          disabled={
                            busy ||
                            isSaving ||
                            isLoading ||
                            proposalSaveBlocked ||
                            isSetupSourcePending ||
                            (failedBuildRetryVm !== null &&
                              (snapshotBusyThreadId !== null ||
                                failedBuildRetryBlocked ||
                                hasUnsavedProfileChanges))
                          }
                          onClick={() =>
                            void (failedBuildRetryVm === null
                              ? prepareBuildWithAgent()
                              : createSnapshot(failedBuildRetryVm.threadId))
                          }
                        >
                          {failedBuildRetryVm === null ? "Prepare again" : "Try Build again"}
                        </Button>
                      ) : null}
                      {canDelete ? (
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => void confirmDeleteBuild(build)}
                          aria-label={`Delete VM Build ${build.id}`}
                        >
                          <Trash2Icon aria-hidden className="size-3.5" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {buildPreparationBootstrapping ||
          (buildPreparationVmDetected && proposalCompletedAt === null && !proposalRunning) ? (
            <div
              className="mt-3 flex items-start gap-2.5 rounded-lg border border-warning/30 bg-warning/8 p-3"
              role="status"
            >
              <Badge size="sm" variant="warning">
                Preparing VM
              </Badge>
              <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                Fabrica Code is creating the guest and running the saved install, start, and
                verification recipe. The agent opens inside the VM when this pass finishes, even if
                a recipe phase fails.
              </p>
            </div>
          ) : buildPreparationActive && proposalRunning ? (
            <div
              className="mt-3 flex items-start gap-2.5 rounded-lg border border-warning/30 bg-warning/8 p-3"
              role="status"
            >
              <Badge size="sm" variant="warning">
                Agent working
              </Badge>
              <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                The preparation agent is validating the development environment inside the VM.
                Create Build unlocks after the turn finishes and the recipe is saved.
              </p>
            </div>
          ) : null}
          {!runningVmsLoaded ? (
            <p className="mt-3 text-xs text-muted-foreground" role="status">
              Checking for a running Build preparation…
            </p>
          ) : buildPreparationActive &&
            runningVms.length === 0 &&
            !buildPreparationBootstrapping &&
            !proposalRunning ? (
            <p className="mt-3 text-xs text-muted-foreground" role="status">
              {proposalCompletedAt === null
                ? "Build preparation is ready. Send the prepared prompt to create and validate the VM."
                : "Build preparation finished without an available running VM. Review the agent result before trying again."}
            </p>
          ) : runningVms.length === 0 ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/70 bg-muted/20 p-3">
              <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                Fabrica Code opens a preparation VM at an exact committed revision and keeps this
                Environment surface attached while the agent starts, fixes, and verifies the
                development environment.
              </p>
              <Button
                size="xs"
                variant="outline"
                disabled={isSaving || isLoading || proposalSaveBlocked || isSetupSourcePending}
                onClick={() => void prepareBuildWithAgent()}
              >
                {isSaving
                  ? "Saving…"
                  : isSetupSourcePending
                    ? "Resolving source…"
                    : hasUnsavedProfileChanges
                      ? "Save & prepare Build"
                      : builds.length === 0
                        ? "Prepare Build with agent"
                        : "Prepare new Build"}
              </Button>
            </div>
          ) : null}
          {profile !== null && runningVms.length > 0 ? (
            <div className="mt-3 space-y-2 rounded-lg border border-border/70 bg-muted/20 p-3">
              <p className="text-xs text-muted-foreground">
                A running VM task is attached to this project. Create a candidate Build from its
                verified sandbox; it will not become active until you activate it below.
              </p>
              {runningVms.map((runningVm) => {
                const thread = projectThreads.find(
                  (candidate) => candidate.id === runningVm.threadId,
                );
                const snapshotBlockReason =
                  thread === undefined
                    ? null
                    : getVmSnapshotBlockReason(thread, { now: snapshotGuardNow });
                const snapshotProfileBlockCopy = proposalSaveBlocked
                  ? "Resolve the agent's blocked proposal before creating a Build."
                  : hasUnsavedProfileChanges
                    ? "Save the current environment recipe before creating a Build."
                    : buildPreparationActive &&
                        runningVm.threadId === proposalThreadId &&
                        proposalCompletedAt === null
                      ? "Wait for VM preparation and the agent turn to finish before creating a Build."
                      : null;
                const snapshotBlockedCopy =
                  snapshotProfileBlockCopy ??
                  (snapshotBlockReason === null ? null : VM_SNAPSHOT_BLOCKED_COPY);
                const snapshotBlockedDescriptionId = `vm-build-blocked-${runningVm.threadId}`;

                return (
                  <div key={runningVm.threadId} className="flex flex-wrap items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger render={<span className="min-w-0 flex-1 truncate text-xs" />}>
                        {runningVm.title}
                      </TooltipTrigger>
                      <TooltipPopup side="top" className="max-w-sm break-all">
                        {runningVm.title}
                      </TooltipPopup>
                    </Tooltip>
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={snapshotBusyThreadId !== null || snapshotBlockedCopy !== null}
                      aria-describedby={
                        snapshotBlockedCopy === null ? undefined : snapshotBlockedDescriptionId
                      }
                      onClick={() => void createSnapshot(runningVm.threadId)}
                    >
                      {snapshotBusyThreadId === runningVm.threadId ? "Creating…" : "Create Build"}
                    </Button>
                    {snapshotBlockedCopy !== null ? (
                      <p
                        id={snapshotBlockedDescriptionId}
                        role="status"
                        className="basis-full text-xs text-muted-foreground"
                      >
                        {snapshotBlockedCopy}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
          <p className="mt-3 text-xs text-muted-foreground">
            Snapshotting is available only when a running VM task for this project reports a
            verified sandbox identity. No snapshot action is shown for stopped, unavailable, or
            ambiguous tasks.
          </p>
        </SettingsRow>
      </fieldset>
      <VmSecretsDialog
        open={secretsDialogOpen}
        onOpenChange={setSecretsDialogOpen}
        existingSecretNames={config.secretNames}
        onSave={saveSecrets}
      />
    </SettingsSection>
  );
}
